import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Trash2, Send, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { useQuote } from "../context/QuoteCart";
import { postQuote } from "../api";
import { CONTACT } from "../mock";
import { WhatsappIcon, Honeypot } from "./ui-kit";
import { toast } from "sonner";

const inputCls =
  "w-full h-11 px-4 bg-canvas border border-line text-sm text-ink focus:outline-none focus:border-ink focus:bg-white placeholder:text-mute transition-colors";

const QuoteBar = () => {
  const { items, remove, clear, open, setOpen, count } = useQuote();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", note: "", website: "", consent: false });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWaMessage = () => {
    const lines = ["Merhaba Koşan Düğme, aşağıdaki ürünler için teklif almak istiyorum:", ""];
    items.forEach((it, i) => {
      lines.push(`${i + 1}. ${it.name}${it.category ? ` (${it.category})` : ""}`);
    });
    const details = [];
    if (form.name.trim()) details.push(`Ad Soyad: ${form.name.trim()}`);
    if (form.company.trim()) details.push(`Firma: ${form.company.trim()}`);
    if (form.phone.trim()) details.push(`Telefon: ${form.phone.trim()}`);
    if (form.note.trim()) details.push(`Not: ${form.note.trim()}`);
    if (details.length) lines.push("", ...details);
    return lines.join("\n");
  };

  const sendWhatsapp = () => {
    if (items.length === 0) {
      toast.error("Teklif sepetiniz boş.");
      return;
    }
    const url = `${CONTACT.whatsappHref}?text=${encodeURIComponent(buildWaMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("WhatsApp hazır mesajınızla açılıyor…");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) {
      toast.error("Lütfen ad soyad ve geçerli bir e-posta girin.");
      return;
    }
    if (items.length === 0) {
      toast.error("Teklif sepetiniz boş.");
      return;
    }
    if (!form.consent) {
      toast.error("Devam etmek için Aydınlatma Metni onayını işaretleyin.");
      return;
    }
    setSending(true);
    try {
      await postQuote({
        ...form,
        items: items.map((i) => ({ name: i.name, category: i.category, code: i.code })),
      });
      toast.success("Teklif talebiniz alındı! En kısa sürede size dönüş yapacağız.");
      clear();
      setForm({ name: "", company: "", email: "", phone: "", note: "", website: "", consent: false });
      setOpen(false);
    } catch (err) {
      toast.error(err.message || "Bir hata oluştu, lütfen tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {count > 0 && (
        <button
          onClick={() => setOpen(true)}
          data-testid="quote-fab"
          className="fixed z-40 bottom-6 right-6 h-14 pl-5 pr-6 rounded-full bg-ink text-white shadow-[0_18px_44px_rgba(26,26,26,0.28)] hover:bg-brand transition-colors duration-300 flex items-center gap-3"
        >
          <span className="relative">
            <ShoppingBag size={20} strokeWidth={1.8} />
            <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-ink" data-testid="quote-fab-count">
              {count}
            </span>
          </span>
          <span className="text-[12px] font-semibold tracking-[0.1em] uppercase">Teklif Sepeti</span>
        </button>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md bg-canvas border-l border-line p-0 flex flex-col"
          data-testid="quote-sheet"
        >
          <SheetTitle className="sr-only">Teklif Sepeti</SheetTitle>
          <div className="px-6 h-[78px] shrink-0 border-b border-line flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-brand">Teklif Sepeti</p>
              <p className="text-ink font-display font-light text-xl mt-0.5">{count} ürün seçildi</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="quote-items">
            {items.length === 0 ? (
              <p className="text-mute text-sm py-10 text-center">Sepetiniz boş. Ürünler sayfasından ürün ekleyin.</p>
            ) : (
              <ul className="space-y-3">
                {items.map((it, i) => (
                  <li
                    key={`${it.category}-${it.code || it.name}`}
                    className="flex items-center gap-3 bg-paper border border-line p-2.5"
                    data-testid={`quote-item-${i}`}
                  >
                    <div className="w-14 h-14 shrink-0 bg-plate overflow-hidden">
                      {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-ink truncate">{it.name}</p>
                      <p className="text-[11px] text-mute uppercase tracking-[0.1em] mt-0.5">{it.category}</p>
                    </div>
                    <button
                      onClick={() => remove(it)}
                      aria-label="Ürünü çıkar"
                      data-testid={`quote-remove-${i}`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-mute hover:text-brand hover:bg-canvas transition-colors shrink-0"
                    >
                      <Trash2 size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <form onSubmit={submit} noValidate className="shrink-0 border-t border-line bg-paper p-6 space-y-3" data-testid="quote-form">
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Ad Soyad *" value={form.name} onChange={set("name")} data-testid="quote-name-input" />
                <input className={inputCls} placeholder="Firma" value={form.company} onChange={set("company")} data-testid="quote-company-input" />
                <input type="email" className={inputCls} placeholder="E-Posta *" value={form.email} onChange={set("email")} data-testid="quote-email-input" />
                <input className={inputCls} placeholder="Telefon" value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" />
              </div>
              <textarea
                rows={2}
                className="w-full px-4 py-2.5 bg-canvas border border-line text-sm text-ink focus:outline-none focus:border-ink focus:bg-white placeholder:text-mute transition-colors resize-none"
                placeholder="Ölçü, adet veya kaplama notunuz…"
                value={form.note}
                onChange={set("note")}
                data-testid="quote-note-input"
              />
              <Honeypot value={form.website} onChange={set("website")} />
              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="quote-consent">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  data-testid="quote-consent-checkbox"
                  className="mt-0.5 w-4 h-4 accent-[#D9261C] shrink-0"
                />
                <span className="text-[11.5px] text-ink2 leading-relaxed">
                  <Link to="/kvkk-aydinlatma-metni" target="_blank" className="text-ink hover:text-brand transition-colors underline">Aydınlatma Metni</Link>
                  'ni okudum; verilerimin teklif talebim için işlenmesini kabul ediyorum.
                </span>
              </label>
              <div className="flex items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-submit-button"
                  className="group flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand hover:bg-brandDark disabled:opacity-60 text-white text-[13px] font-semibold transition-colors"
                >
                  {sending ? "Gönderiliyor…" : `Toplu Teklif İste (${count})`}
                  <Send size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  type="button"
                  onClick={clear}
                  data-testid="quote-clear-button"
                  className="h-12 px-4 rounded-full border border-line text-[12px] font-semibold text-ink2 hover:border-ink hover:text-ink transition-colors"
                >
                  Temizle
                </button>
              </div>
              <div className="relative flex items-center justify-center py-0.5">
                <span className="h-px w-full bg-line" />
                <span className="absolute px-3 bg-paper text-[11px] uppercase tracking-[0.2em] text-mute">veya</span>
              </div>
              <button
                type="button"
                onClick={sendWhatsapp}
                data-testid="quote-whatsapp-button"
                className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white text-[13px] font-semibold transition-colors"
              >
                <WhatsappIcon size={16} /> WhatsApp'tan Hazır Mesajla Gönder
              </button>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default QuoteBar;
